import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useLazyViewSolarPlantStatusesQuery } from "../../../redux/api/dashboard/dashboardAPI";
import { useLazyViewGenerationServiceProvidersQuery } from "../../../redux/api/generation/generationAPI";
import SitesErrorLogTable from "../../../shared/components/site-error-log-table/site-error-log-table";
import IViewAllGenerationServiceProvidersResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-all-generation-service-providers-response-dto";
import ViewSolarPlantStatusesResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-solar-plant-statuses-response-dto";
import { ESolarServiceProvider } from "../../../shared/oversight-core/enums/solar-service-provider";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import { IGenerationServiceProviderView } from "../../../shared/oversight-core/interfaces/generation-service-provider-view";
import { IGoodWeSolarPlantAlertData } from "../../../shared/oversight-core/interfaces/good-we-solar-plant-alert-data";
import { ISolarPlantAlerts } from "../../../shared/oversight-core/interfaces/solar-plant-alerts";
import { Option } from "../../../shared/oversight-core/ui-elements/app-select/app-select";
import ArrowButton from "../../../shared/oversight-core/ui-elements/arrow-button/arrow-button";
import ButtonWithBadge from "../../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import MaterialIcon from "../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Search from "../../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import { getServiceProviderLogo } from "../../../shared/oversight-core/utils/get-service-provider-logo";
import {
  formatEpochToDate,
  getTimestampDuration,
} from "../../../shared/oversight-core/utils/time-utils";
import SitesErrorLogFilterPanel from "./sites-error-log-filter-panel/sites-error-log-filter-panel";

interface ISitesErrorLog {
  plantName: string;
  plantTimeZone: string;
  plantIdentity: string;
  generationServiceProviderId: string;
  vrmAlert: ISolarPlantAlerts;
  goodWeAlert: IGoodWeSolarPlantAlertData;
}

interface ISearchFilters {
  extended?: boolean;
  generationServiceProviderId?: string;
  searchValue?: string;
}

const initialFilters: ISearchFilters = {
  generationServiceProviderId: "",
  searchValue: "",
};

const SitesErrorLog = () => {
  const navigate = useNavigate();

  const [solarPlantStatuses, setSolarPlantStatuses] = useState<
    ISitesErrorLog[]
  >([]);
  const [showFilteringSidePanel, setShowFilteringSidePanel] = useState(false);
  const [filterBadgeValue, setFilterBadgeValue] = useState(0);
  const [solarBrandList, setSolarBrandList] = useState<Option[]>([
    { value: "", label: "", data: "" },
  ]);
  const [filters, setFilters] = useState({ ...initialFilters });
  const [searchText, setSearchText] = useState("");

  const [
    triggerSolarPlantStatuses,
    { isFetching: isFetchingSolarPlantStatuses },
  ] = useLazyViewSolarPlantStatusesQuery();
  const [triggerGenerationServiceProviders] =
    useLazyViewGenerationServiceProvidersQuery();

  const updateFilters = (generationServiceProviderId?: string) => {
    setFilters({
      generationServiceProviderId,
    });
  };

  const onSearch = () => {
    if (searchText) {
      setFilters({
        searchValue: searchText,
      });
    }
  };

  useEffect(() => {
    const filterParams: ISearchFilters = {
      extended: true,
      searchValue: filters.searchValue,
    };

    if (filters.generationServiceProviderId) {
      filterParams.generationServiceProviderId =
        filters.generationServiceProviderId;
    }

    triggerSolarPlantStatuses({ ...filterParams })
      .unwrap()
      .then((res: ViewSolarPlantStatusesResponseDTO) => {
        const alerts: ISitesErrorLog[] = res.solarPlantStatuses.flatMap(
          (site) =>
            site.solarPlantAlerts.map((alert) => ({
              plantName: site.plantName,
              plantTimeZone: site.plantTimeZone,
              plantIdentity: site.plantIdentity,
              generationServiceProviderId:
                site.generationServiceProvider.identity,
              vrmAlert: alert as ISolarPlantAlerts,
              goodWeAlert: alert as IGoodWeSolarPlantAlertData,
            }))
        );
        setSolarPlantStatuses(alerts);
      })
      .catch((error) => {
        setSolarPlantStatuses([]);
      });
  }, [triggerSolarPlantStatuses, filters]);

  useEffect(() => {
    triggerGenerationServiceProviders()
      .unwrap()
      .then((res: IViewAllGenerationServiceProvidersResponseDTO) => {
        const mappingSolarBrandList = res.generationServiceProviders.map(
          (g: IGenerationServiceProviderView) => ({
            value: g.identity,
            label: g.name,
            data: g.shortName,
          })
        );
        setSolarBrandList(mappingSolarBrandList);
      })
      .catch(() => {
        setSolarBrandList([]);
      });
  }, [triggerGenerationServiceProviders]);

  useEffect(() => {
    if (!searchText) {
      setFilters({ ...initialFilters });
    }
  }, [searchText]);

  const getServiceProvider = (id: string): ESolarServiceProvider => {
    const findServiceProvider = solarBrandList.find(
      (item) => item.value === id
    );

    return findServiceProvider?.data as ESolarServiceProvider;
  };

  return (
    <div className="position-relative">
      <Row className="align-items-center mt-3">
        <Col className="col-auto">
          <Row className="mx-0 align-items-center">
            <Col className="col-auto">
              <ArrowButton onBack={() => navigate(-1)} />
            </Col>
            <Col className="text-dark font-weight-500 font-size-24 ps-3">
              Error Log
            </Col>
          </Row>
        </Col>
        <Col className="col-12 col-sm mt-2 mt-sm-0">
          <Row className="justify-content-end">
            <Col className="col-8 col-sm-8 col-lg-5 col-xl-4">
              <Search
                onSearch={onSearch}
                handleSearchChange={(searchText) => {
                  setSearchText(searchText);
                }}
                placeholder="Search by name or address"
                value={searchText}
              />
            </Col>
            <Col className="col-4 col-sm-auto">
              <ButtonWithBadge
                badgeValue={filterBadgeValue}
                text="Filter"
                icon="filter_alt"
                onClick={() => setShowFilteringSidePanel(true)}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <SitesErrorLogTable
            headers={[
              { id: 1, columnName: "Start Time" },
              { id: 2, columnName: "Site" },
              { id: 4, columnName: "Description" },
              { id: 5, columnName: "Cleared After" },
            ]}
            data={solarPlantStatuses.map((i) => {
              return {
                data: [
                  <>
                    {getServiceProvider(i.generationServiceProviderId) ===
                    ESolarServiceProvider.VRM
                      ? formatEpochToDate(
                          i.vrmAlert.data.started,
                          i.plantTimeZone
                        )
                      : moment
                          .utc(
                            i.goodWeAlert.data.status
                              ? i.goodWeAlert.data.recoverytime
                              : i.goodWeAlert.data.happentime
                          )
                          .format("YYYY/MM/DD HH:mm:ss")}
                  </>,
                  <>
                    <Row className="align-items-center row-cols-auto">
                      <Col className="pe-0">
                        <img
                          src={getServiceProviderLogo(
                            getServiceProvider(i.generationServiceProviderId)
                          )}
                          alt=""
                        />
                      </Col>
                      <Col className="ps-2 text-light font-weight-400 font-size-12">
                        {i.plantName}
                      </Col>
                    </Row>
                  </>,
                  <>
                    {getServiceProvider(i.generationServiceProviderId) ===
                    ESolarServiceProvider.VRM
                      ? i.vrmAlert.data.description
                      : i.goodWeAlert.data.warningname}
                  </>,
                  <>
                    <Row className="justify-content-between me-2">
                      <Col>
                        {getServiceProvider(i.generationServiceProviderId) ===
                        ESolarServiceProvider.VRM
                          ? !i.vrmAlert.data.cleared
                            ? `Active`
                            : i.vrmAlert.data.started && i.vrmAlert.data.cleared
                            ? getTimestampDuration(
                                i.vrmAlert.data.started,
                                i.vrmAlert.data.cleared
                              )
                            : "-"
                          : "Active"}
                      </Col>
                      <Col className="col-auto">
                        <MaterialIcon
                          icon="arrow_right_alt"
                          color="#1A3C84"
                          size={18}
                          weight={500}
                        />
                      </Col>
                    </Row>
                  </>,
                ],
                cellWidths: [150, 250, 550, 150],
                isActive: false,
                onClick: () => {
                  navigate(AppRoute.ADMIN_SITES + "/" + i.plantIdentity, {
                    state: {
                      id: i.plantIdentity,
                      serviceProvider: getServiceProvider(
                        i.generationServiceProviderId
                      ),
                    },
                  });
                },
              };
            })}
          />
        </Col>
      </Row>
      <SitesErrorLogFilterPanel
        show={showFilteringSidePanel}
        onClose={() => setShowFilteringSidePanel(false)}
        updateFilters={updateFilters}
        setFilterBadgeValue={setFilterBadgeValue}
        filters={filters}
        solarBrandList={solarBrandList}
      />
      <SpinnerModal show={isFetchingSolarPlantStatuses} />
    </div>
  );
};

export default SitesErrorLog;
